import React, { FC, useMemo } from "react";

import { useProvidersCurrentEmployee } from "~/hooks";

import { getManagerNavigationConfig } from "~/config/NavigationConfig";

import { SidebarLink } from "./SidebarLink";
import { SidebarSectionHeader } from "./SidebarSectionHeader";

import {
  ApiMembersUnreadNotificationCountsType,
  NavigationItemCompanyType,
  OwnerRole,
} from "~/domains";

import { Accordion } from "~/ui";

type PropsType = {
  breadCrumbText: string;
  unreadNotification?: ApiMembersUnreadNotificationCountsType;
  navigationItems?: NavigationItemCompanyType[];
};

export const ManagerSidebar: FC<PropsType> = ({
  breadCrumbText,
  unreadNotification,
  navigationItems,
}: PropsType) => {
  const { currentEmployee } = useProvidersCurrentEmployee();
  const defaultBusinessSectionId =
    currentEmployee?.businessSections[0]?.id || "";

  const navigationConfig = useMemo(
    () => getManagerNavigationConfig(defaultBusinessSectionId),
    [defaultBusinessSectionId],
  );

  const displayNavs = navigationItems
    ?.filter((item) => item.role === "manager" && !item.hiddenAt)
    .sort((a, b) => a.position - b.position);

  return (
    <>
      {navigationConfig.map((group, groupIndex) => {
        const groupNavs =
          displayNavs?.filter((nav) => nav.name in group.items) || [];
        if (groupNavs.length < 1) return null;

        return (
          <React.Fragment key={group.section}>
            {groupIndex > 0 && <SidebarSectionHeader title={group.section} />}
            {groupNavs.map((nav) => {
              const config = group.items[nav.name];
              const isActive = breadCrumbText === nav.name;

              if (config?.lists) {
                return (
                  <Accordion
                    key={nav.id}
                    buttonClassName={`mx-2 rounded-lg text-sm px-3 py-2 font-medium truncate ${
                      isActive
                        ? "bg-primary-600 text-white"
                        : "text-secondary-900 hover:bg-primary-300"
                    }`}
                    iconColor={isActive ? "text-white" : "text-secondary-600"}
                    title={nav.name}
                    icon={config.icon}
                    lists={config.lists.filter((list) =>
                      list.isOwnerOnly
                        ? currentEmployee?.employeeRole.type === OwnerRole.type
                        : true,
                    )}
                    isDefaultOpen={isActive}
                  />
                );
              }

              if (config?.href) {
                return (
                  <SidebarLink
                    key={nav.id}
                    label={nav.name}
                    href={config.href}
                    breadCrumbText={breadCrumbText}
                    unreadNotification={unreadNotification}
                    icon={config.icon}
                  />
                );
              }

              return null;
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};
